// FLAGS: personal-data
// Per-session attendance for a workplace session. The stub note that stood here said "who attended a
// wellbeing session is personal data, and none of it is held here yet" — the second half expired with
// this change. The roster is a named list of identified people, and the export takes it off the
// platform onto a staff machine as a file nothing here can recall. See components/spaces/MODULE.md
// for why this is personal-data and not health-data, and utils/csv.ts for why the export escapes.
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";
import { Link, useParams } from "react-router";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import FormField from "@src/components/spaces/FormField";
import { Select } from "@src/components/spaces/ui";
import AttendanceSummary from "@src/components/spaces/AttendanceSummary";
import AttendanceFunnel from "@src/components/spaces/AttendanceFunnel";
import AttendanceRoster from "@src/components/spaces/AttendanceRoster";
import { formatSessionDateTime, formatSessionTime } from "@src/utils/format";
import { downloadCsv, toCsv } from "@src/utils/csv";
import { useEvent, useSessionAttendance } from "@src/queries/events";

const sessionLabel = (startsAt: string, endsAt: string): string =>
  `${formatSessionDateTime(startsAt)}–${formatSessionTime(endsAt)}`;

const CSV_HEADERS = [
  "Name",
  "Email",
  "Employer",
  "Registered at",
  "Status",
  "Joined at",
  "Duration (seconds)",
  "Attendance %",
];

const EventAttendancePage = () => {
  const { id = "" } = useParams();
  const [selectedId, setSelectedId] = useState("");
  const { data: event, isLoading: eventLoading } = useEvent(id);

  const sessions = event?.sessions ?? [];
  // Attendance is per session; default to the first until the user picks one.
  const sessionId = selectedId || sessions[0]?.id || "";

  const {
    data: attendance,
    isLoading: attendanceLoading,
    isError,
  } = useSessionAttendance(sessionId);

  const onExport = () => {
    if (!attendance) {
      return;
    }
    const rows = attendance.roster.map((row) => [
      row.name,
      row.email,
      row.employer,
      row.registeredAt,
      row.status,
      row.joinedAt,
      row.durationSeconds,
      row.attendanceRatePercent,
    ]);
    downloadCsv(`attendance-${sessionId}.csv`, toCsv(CSV_HEADERS, rows));
  };

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        to={`/events/${id}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to event
      </Link>

      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-indigo-800">Attendance</h1>
        <Button
          variant="outline"
          onClick={onExport}
          disabled={!attendance || attendance.roster.length === 0}
        >
          <Download className="mr-1 h-4 w-4" aria-hidden />
          Export CSV
        </Button>
      </div>

      {eventLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {!eventLoading && sessions.length === 0 && (
        <p className="text-gray-600">This event has no sessions yet.</p>
      )}

      {!eventLoading && sessions.length > 0 && (
        <>
          {sessions.length > 1 ? (
            <div className="mb-6">
              <FormField label="Session">
                {({ id: selectId }) => (
                  <Select
                    id={selectId}
                    value={sessionId}
                    onChange={(e) => setSelectedId(e.target.value)}
                  >
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.title} ·{" "}
                        {sessionLabel(session.startsAt, session.endsAt)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </div>
          ) : (
            <p className="mb-6 text-sm text-gray-600">
              {sessions[0].title} ·{" "}
              {sessionLabel(sessions[0].startsAt, sessions[0].endsAt)}
            </p>
          )}

          {attendanceLoading && (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          )}

          {isError && (
            <p className="text-red-600">Couldn&apos;t load attendance.</p>
          )}

          {attendance && !attendanceLoading && (
            <div className="flex flex-col gap-6">
              <AttendanceSummary counts={attendance.counts} />
              <AttendanceFunnel funnel={attendance.funnel} />
              <AttendanceRoster roster={attendance.roster} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventAttendancePage;
